import { useSettings } from '@/api/hooks'
import { usePlatformStatusStore } from '@/stores/platformStatus'

/**
 * Known-issue banner (CLA-936).
 *
 * Written after the 2026-08-10 transcription degradation, where notes took
 * 2–5.5 minutes instead of ~5s and the app simply span — no explanation, and no
 * way for a clinician to tell whether their consult was safe. That is the
 * moment somebody re-records a consultation that was never at risk.
 *
 * Two design rules carry this component:
 *
 * 1. **The verdict is words, not colour.** Severity tints the banner, but what
 *    the clinician actually needs — "is my consult safe?" — is stated in a chip.
 *    Colour alone fails for colour-vision deficiency and fails entirely at a
 *    glance across a treatment room.
 * 2. **An unsafe banner is not dismissible.** Warning and info can be dismissed
 *    for the session; a critical "don't record" cannot, because dismissing it
 *    is precisely the mistake it exists to prevent.
 */
export function PlatformStatusBanner() {
  const { data: settings } = useSettings()
  const wsStatus = usePlatformStatusStore(s => s.wsStatus)
  const dismissedMessage = usePlatformStatusStore(s => s.dismissedMessage)
  const dismiss = usePlatformStatusStore(s => s.dismiss)

  // The socket wins once it has spoken. Until then fall back to the value that
  // came down with settings, so opening mid-incident shows the banner at once.
  const status = wsStatus !== undefined ? wsStatus : (settings?.platform_status ?? null)

  if (!status) return null

  const recordingSafe = status.recording_safe
  // Dismissal is a convenience for "slow but fine", never an escape hatch from
  // "your recording may not be saved".
  const dismissible = recordingSafe
  if (dismissible && dismissedMessage === status.message) return null

  const tone =
    status.severity === 'critical'
      ? {
          wrap: 'bg-error/10 border-b border-error/25',
          rule: 'bg-error',
          text: 'text-on-error-soft',
          icon: 'text-error',
        }
      : status.severity === 'info'
        ? {
            wrap: 'bg-info-light border-b border-info/25',
            rule: 'bg-info',
            text: 'text-on-info-soft',
            icon: 'text-info',
          }
        : {
            wrap: 'bg-warning-light border-b border-warning/25',
            rule: 'bg-warning',
            text: 'text-on-warning-soft',
            icon: 'text-warning',
          }

  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex items-stretch shrink-0 ${tone.wrap}`}
      data-testid="platform-status-banner"
    >
      <div className={`w-1 shrink-0 ${tone.rule}`} aria-hidden="true" />
      <div className="flex-1 min-w-0 flex items-start gap-2.5 px-4 py-2">
        <svg
          className={`w-4 h-4 mt-0.5 shrink-0 ${tone.icon}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          {status.severity === 'info' ? (
            <path d="M10 1a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm.9 13.9H9.1V9h1.8v5.9ZM10 7.4a1.1 1.1 0 1 1 0-2.2 1.1 1.1 0 0 1 0 2.2Z" />
          ) : (
            <path d="M10 1.6 19 18H1L10 1.6Zm0 5.6a.9.9 0 0 0-.9 1v3.6a.9.9 0 0 0 1.8 0V8.2a.9.9 0 0 0-.9-1Zm0 7.1a1.05 1.05 0 1 0 0 2.1 1.05 1.05 0 0 0 0-2.1Z" />
          )}
        </svg>

        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          <p className={`text-[13px] font-medium leading-snug ${tone.text}`}>{status.message}</p>

          {/* The load-bearing line. Stated in words so it survives colour
              blindness, a glance, and a greyscale screenshot in a bug report. */}
          <span
            className={
              recordingSafe
                ? 'self-start inline-flex items-center gap-1.5 rounded-full border border-success/30 bg-success/10 px-2 py-0.5 text-[11px] font-semibold text-on-success-soft'
                : 'self-start inline-flex items-center gap-1.5 rounded-full border border-error/35 bg-error/15 px-2 py-0.5 text-[11px] font-semibold text-on-error-soft'
            }
            data-testid="platform-status-verdict"
          >
            <svg className="w-3 h-3 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              {recordingSafe ? (
                <path d="M8.2 13.4 4.8 10l-1.3 1.3 4.7 4.7 8.5-8.5-1.3-1.3-7.2 7.2Z" />
              ) : (
                <path d="M10 1a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm4 12.7-1.3 1.3L10 11.3 7.3 14 6 12.7 8.7 10 6 7.3 7.3 6 10 8.7 12.7 6 14 7.3 11.3 10 14 12.7Z" />
              )}
            </svg>
            {recordingSafe
              ? 'Safe to keep recording — consults are saved'
              : 'New recordings may not be saved'}
          </span>
        </div>

        {dismissible && (
          <button
            type="button"
            onClick={() => dismiss(status.message)}
            aria-label="Dismiss for this session"
            className={`shrink-0 -mr-1 rounded p-1 opacity-60 hover:opacity-100 hover:bg-black/5 transition ${tone.text}`}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M15.3 6 14 4.7 10 8.7 6 4.7 4.7 6l4 4-4 4L6 15.3l4-4 4 4 1.3-1.3-4-4 4-4Z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}
